import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  getBulkImgList,
  updateBulkImgListByID,
} from "actions/bulk-upload-action";
import { deleteImageById } from "database/offlineDb/indexDb";
import {
  getBulkEventTuple,
  getBulkRowTuples,
  isBulkRetryableStatus,
  isBulkSettlementTerminalStatus,
  projectBulkStatusEvent,
} from "./bulkUploadProjection";
import { createBulkUploadStatusAdapter } from "./bulkUploadStatusAdapter";
import { persistBulkStatusProjection } from "./bulkUploadPersistence";
import { resyncBulkUploadStatuses } from "./bulkUploadSnapshot";
import { useMlStatusRepository } from "./mlStatusContext";

const nonemptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

const isSettlementTerminalRow = (row) => {
  if (!Array.isArray(row?.status) || row.status.length === 0) return false;
  const tuples = getBulkRowTuples(row);
  return (
    tuples.length === row.status.length &&
    row.status.every((status) =>
      isBulkSettlementTerminalStatus(status?.status)
    )
  );
};

const trackedTupleEvidence = (rows, previousTuples = []) => {
  if (!Array.isArray(rows)) return null;
  const availableTuples = new Set();
  const retryableRowTuples = [];
  const terminalRowTuples = [];
  for (const row of rows) {
    if (!Array.isArray(row?.status)) continue;
    const rowTuples = getBulkRowTuples(row);
    if (rowTuples.length !== row.status.length) return null;
    rowTuples.forEach((tuple) => availableTuples.add(tuple));
    if (row.status.some((status) => isBulkRetryableStatus(status?.status))) {
      retryableRowTuples.push(...rowTuples);
    }
    if (isSettlementTerminalRow(row)) terminalRowTuples.push(...rowTuples);
  }
  const retainedTuples = previousTuples.filter((tuple) =>
    availableTuples.has(tuple)
  );
  const trackedTuples = Array.from(
    new Set([...retainedTuples, ...retryableRowTuples, ...terminalRowTuples])
  ).sort();
  return trackedTuples.length > 0 ? trackedTuples : null;
};

export const BulkUploadRealtimeOwner = ({
  adapterFactory = createBulkUploadStatusAdapter,
  authToken,
  location,
  orgId,
  publish,
  republish,
  resync,
  rows = [],
  deleteRow = deleteImageById,
}) => {
  const repository = useMlStatusRepository();
  const trackedTuplesRef = useRef([]);
  const authTokenRef = useRef(authToken);
  const authGenerationRef = useRef(0);
  if (authTokenRef.current !== authToken) {
    authTokenRef.current = authToken;
    authGenerationRef.current += 1;
    trackedTuplesRef.current = [];
  }
  const canTrack =
    location?.pathname === "/bulk-upload" &&
    nonemptyString(authToken) &&
    orgId !== undefined &&
    orgId !== null &&
    typeof publish === "function" &&
    typeof resync === "function";
  const tuples = canTrack
    ? trackedTupleEvidence(rows, trackedTuplesRef.current)
    : null;
  trackedTuplesRef.current = tuples || [];
  const active =
    canTrack && tuples;
  const scopeKey = active
    ? `bulk-upload:${String(orgId)}:${tuples.join(
        ","
      )}:auth-${authGenerationRef.current}`
    : null;
  const rowsRef = useRef(rows);
  const adapterFactoryRef = useRef(adapterFactory);
  const publishRef = useRef(publish);
  const republishRef = useRef(republish);
  const resyncRef = useRef(resync);
  const deleteRowRef = useRef(deleteRow);
  rowsRef.current = rows;
  adapterFactoryRef.current = adapterFactory;
  publishRef.current = publish;
  republishRef.current = republish;
  resyncRef.current = resync;
  deleteRowRef.current = deleteRow;

  useEffect(() => {
    if (!repository || !scopeKey) return undefined;
    let eventGeneration = 0;
    let snapshotGeneration = 0;
    let statusWrites = Promise.resolve();
    let terminalRetirements = Promise.resolve();
    const deletedGuids = new Set();
    const persistInOrder = (options) => {
      const operation = statusWrites.catch((error) => { console.warn("BulkUploadRealtimeOwner.js queue continuation suppressed previous failure", error); }).then(() => {
        if (
          typeof options?.isCurrent === "function" &&
          !options.isCurrent()
        ) {
          return null;
        }
        return persistBulkStatusProjection(options);
      });
      statusWrites = operation;
      return operation;
    };
    const acceptedTuples = new Set(tuples);
    const adapter = adapterFactoryRef.current({
      persist: persistInOrder,
      publish: (row) => publishRef.current?.(row),
    });
    const retireTerminalRows = (candidateRows, context) => {
      const operation = terminalRetirements
        .catch((error) => {
          console.warn(
            "BulkUploadRealtimeOwner.js terminal continuation suppressed previous failure",
            error
          );
        })
        .then(async () => {
          if (
            !context.isCurrent() ||
            typeof deleteRowRef.current !== "function" ||
            typeof republishRef.current !== "function"
          ) {
            return false;
          }
          const rowsToEvaluate = Array.isArray(candidateRows)
            ? candidateRows
            : [];
          rowsToEvaluate.forEach((row) => {
            if (isSettlementTerminalRow(row)) return;
            const guid = row?.guid || row?.info?.guid;
            if (nonemptyString(guid)) deletedGuids.delete(guid);
          });
          const terminalRows = rowsToEvaluate.filter(isSettlementTerminalRow);
          if (terminalRows.length === 0) return false;
          for (const row of terminalRows) {
            if (!context.isCurrent()) return false;
            const guid = row?.guid || row?.info?.guid;
            if (!nonemptyString(guid) || deletedGuids.has(guid)) continue;
            await deleteRowRef.current(guid);
            deletedGuids.add(guid);
          }
          if (!context.isCurrent()) return false;
          await republishRef.current(context);
          return true;
        });
      terminalRetirements = operation;
      return operation;
    };
    const runSnapshot = async (context) => {
      const acceptedEventGeneration = eventGeneration;
      const acceptedSnapshotGeneration = ++snapshotGeneration;
      const snapshotContext = {
        ...context,
        isCurrent: () =>
          context.isCurrent() &&
          eventGeneration === acceptedEventGeneration &&
          snapshotGeneration === acceptedSnapshotGeneration,
      };
      if (
        typeof resyncRef.current !== "function" ||
        !snapshotContext.isCurrent()
      ) {
        return { outcome: "ignored" };
      }
      const result = await resyncRef.current({
        authToken: authTokenRef.current,
        orgId,
        rows: rowsRef.current,
        signal: snapshotContext.signal,
        isCurrent: snapshotContext.isCurrent,
        persist: persistInOrder,
      });
      if (
        snapshotContext.isCurrent() &&
        (result?.outcome === "applied" || result?.outcome === "ignored")
      ) {
        await retireTerminalRows(result.rows, snapshotContext);
      }
      return result;
    };
    const unsubscribe = repository.subscribe({
      scopeKey,
      accepts: (event) => {
        const eventTuple = getBulkEventTuple(event);
        return (
          acceptedTuples.has(eventTuple) &&
          projectBulkStatusEvent(rowsRef.current, event).outcome !== "ignored"
        );
      },
      initialSnapshot: runSnapshot,
      project: async (event, context) => {
        eventGeneration += 1;
        const result = await adapter.apply({
          getRows: () => rowsRef.current,
          event,
          isCurrent: context.isCurrent,
        });
        if (result?.outcome === "applied" && context.isCurrent()) {
          try {
            await retireTerminalRows([result.row], context);
          } catch (error) {
            return { outcome: "fallback" };
          }
        }
        return result;
      },
      resync: runSnapshot,
    });
    return () => {
      unsubscribe();
      adapter.cleanup();
    };
  }, [repository, scopeKey]);

  return null;
};

const mapStateToProps = (store) => ({
  orgId: store.userStore?.userProfile?.orgz_id,
  rows: store.bulkUploadImageStore?.bulkUploadList || [],
});

export default connect(mapStateToProps, {
  publish: updateBulkImgListByID,
  republish: getBulkImgList,
  resync: resyncBulkUploadStatuses,
})(withRouter(BulkUploadRealtimeOwner));
